import moment from 'moment';
import { FormattedMessage } from 'react-intl';

// Components
import {
  Box,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';

// Hooks
import useSelectFromWaitlistModal from './hooks';

// Messages
import messages from '../messages';

const renderReadOnlyField = ({ label, tooltip, value }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      <Typography variant={'labelLarge'}>{label}</Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="right">
          <AlertInfoIcon fontSize="small" />
        </Tooltip>
      )}
    </Box>
    <Box
      sx={{
        bgcolor: 'common.white',
        borderRadius: '8px',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        px: '12px',
        py: '8px',
      }}
    >
      <Typography variant={'bodyMedium'}>{value}</Typography>
    </Box>
  </>
);

const SelectFromWaitlistModal = ({
  organizationId,
  propertyId,
  // Modal
  open,
  handleClose,
  modalProps,
  // Form
  ReactHookFormProps,
  formProps,
  // Info
  affordableWaitlistRow,
  separateByBedroom,
  waitlistV2Programs,
}) => {
  const { watch } = ReactHookFormProps;
  const selectedFloorPlanId = watch('floorPlanId');

  const {
    isLoadingFloorPlans,
    floorPlanOptions,
    fundingProgramsDisplay,
    floorPlanProgramsDisplay,
    showBedroomsField,
    todayDateUTC,
  } = useSelectFromWaitlistModal({
    organizationId,
    propertyId,
    bedroomCount: affordableWaitlistRow?.bedroomCount ?? null,
    selectedFloorPlanId,
    separateByBedroom,
    waitlistV2Programs,
  });

  const fields = [
    {
      name: 'fundingPrograms',
      variant: 'custom',
      component: renderReadOnlyField({
        label: <FormattedMessage {...messages.fundingPrograms} />,
        tooltip: <FormattedMessage {...messages.fundingProgramsTooltip} />,
        value: fundingProgramsDisplay || '---',
      }),
      GridProps: { xs: 12, sm: 6 },
    },
    showBedroomsField
      ? {
          name: 'bedrooms',
          variant: 'custom',
          component: renderReadOnlyField({
            label: <FormattedMessage {...messages.bedrooms} />,
            value: affordableWaitlistRow?.bedroomCount ?? '---',
          }),
          GridProps: { xs: 12, sm: 6 },
        }
      : {
          // Keeps Qualification Floor Plan / Floor plan programs on their own
          // row instead of the flat Grid packing them next to Funding
          // Programs when Bedrooms isn't rendered.
          name: 'bedroomsSpacer',
          variant: 'custom',
          component: null,
          GridProps: { xs: 12, sm: 6 },
        },
    {
      variant: 'singleSelect',
      name: 'floorPlanId',
      label: <FormattedMessage {...messages.qualificationFloorPlan} />,
      options: floorPlanOptions,
      disabled: isLoadingFloorPlans,
      InputLabelProps: {
        TooltipProps: {
          title: (
            <FormattedMessage {...messages.qualificationFloorPlanTooltip} />
          ),
          placement: 'right',
          children: <AlertInfoIcon fontSize="small" />,
        },
      },
      rules: { required: true },
      GridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'floorPlanPrograms',
      variant: 'custom',
      component: renderReadOnlyField({
        label: <FormattedMessage {...messages.floorPlanPrograms} />,
        tooltip: <FormattedMessage {...messages.floorPlanProgramsTooltip} />,
        value: floorPlanProgramsDisplay,
      }),
      GridProps: { xs: 12, sm: 6 },
    },
    {
      variant: 'date',
      name: 'preferredMoveInDate',
      label: <FormattedMessage {...messages.preferredMoveInDate} />,
      minDate: moment.utc(todayDateUTC),
      InputLabelProps: {
        TooltipProps: {
          title: <FormattedMessage {...messages.preferredMoveInDateTooltip} />,
          placement: 'right',
          children: <AlertInfoIcon fontSize="small" />,
        },
      },
      rules: { required: true },
      GridProps: { xs: 12, sm: 6 },
    },
    ...(formProps?.fields ?? []).map((field) => ({
      ...field,
      GridProps: { xs: 12 },
    })),
  ];

  return (
    <Modal open={open} onClose={handleClose} {...modalProps}>
      <Form ReactHookFormProps={ReactHookFormProps} fields={fields} />
    </Modal>
  );
};

export default SelectFromWaitlistModal;
